import type { ReferralInfo } from '@getstep/sdk/dist/http/models/Referrals'
import analyticsService from '@lib/analytics/AnalyticsService'
import {
    extractQueryParams,
    getServerCookieAttribution,
    GOOGLE_ADS_PARAM_KEYS,
    GOOGLE_IOS_PARAM_KEYS,
    IMPACT_RADIUS_PARAM_KEYS,
    SNAPCHAT_PARAM_KEYS,
    TIKTOK_PARAM_KEYS,
    UTM_PARAM_KEYS,
} from '@lib/analytics/constants'
import googleAdsService from '@lib/analytics/googleAds/GoogleAdsService'
import { getMetaPixelTraits } from '@lib/analytics/metaPixel'
import { AppsFlyerLinker } from '@lib/store/linking/AppsFlyerLinker'
import { useStores } from '@lib/store/useStores'
import { pick } from 'lodash'
import { when } from 'mobx'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

/**
 * ClickTracker Hook - Attribution & Deep Link Parameter Capture
 *
 * Purpose: Captures attribution parameters from URL query strings when users land on pages,
 * particularly for tracking marketing campaigns, referrals, and deep link data.
 *
 * How it works:
 * 1. Monitors URL query parameters when the router is ready
 * 2. Extracts AppsFlyer attribution parameters (utm_source, utm_campaign, pid, etc.)
 * 3. Updates the LinkingStore with this attribution data
 * 4. This data is later used when generating app download links to preserve attribution
 *
 * Use cases:
 * - User clicks a marketing link with UTM parameters → ClickTracker captures them
 * - User later clicks "Download App" → Attribution is preserved in the app link
 * - Referral links with codes → ClickTracker captures referral parameters
 *
 * @param props Optional configuration including feature name and referral info
 * @returns null (this is a side effect only hook)
 */
export const useClickTracker = (props?: ClickTrackerProps) => {
    const { query, isReady } = useRouter()
    const { linkingStore } = useStores()
    const { feature } = props || {}

    useEffect(() => {
        // Wait for Next.js router to be ready with complete query parameters
        if (!isReady) {
            return
        }

        // Capture attribution data IMMEDIATELY (don't wait for AppsFlyer/LinkingStore)
        // This fixes the race condition where conversions fire before attribution is captured
        captureAttributionData()

        // AppsFlyer-specific operations can wait for linkingStore.loaded
        when(
            () => linkingStore.loaded,
            () => {
                // Extract AppsFlyer attribution parameters for deep linking
                const params = AppsFlyerLinker.appsFlyerParams(query)

                // Send attribution event to AppsFlyer web SDK (if available)
                if (Object.keys(params).length > 0) {
                    window.AF?.('pba', 'event', {
                        eventType: 'EVENT',
                        eventName: 'attribution_captured',
                        eventValue: params as any,
                    })
                }

                // Update the linking store with attribution data
                // This preserves the attribution for future app download links
                linkingStore.update(
                    {
                        ...params,
                        feature: feature ?? params.feature,
                    },
                    'feature',
                )
            },
        )

        /**
         * Capture attribution data from URL and server cookies.
         * Sets data in both AnalyticsService and GoogleAdsService for consistency.
         */
        function captureAttributionData() {
            // Capture all UTM parameters from URL
            const utmParams = pick(query, UTM_PARAM_KEYS)

            // Capture Google Ads specific parameters - normalize to strings (take first if array)
            const googleAdsParams = extractQueryParams(
                query,
                GOOGLE_ADS_PARAM_KEYS,
            )

            // Capture Google iOS 14.5+ parameters (wbraid, gbraid)
            const googleIosParams = extractQueryParams(
                query,
                GOOGLE_IOS_PARAM_KEYS,
            )

            // Capture TikTok parameters
            const tiktokParams = extractQueryParams(query, TIKTOK_PARAM_KEYS)

            // Capture Snapchat parameters
            const snapchatParams = extractQueryParams(
                query,
                SNAPCHAT_PARAM_KEYS,
            )

            // Get Meta Pixel traits (fbc, fbp, fbclid)
            const metaPixelTraits = getMetaPixelTraits(
                typeof query.fbclid === 'string' ? query.fbclid : null,
            )

            // Capture Impact Radius click ID (clickid or im_ref)
            const impactClickId = IMPACT_RADIUS_PARAM_KEYS.reduce<
                string | null
            >((acc, key) => {
                if (acc) return acc
                const value = query[key]
                if (value !== undefined) {
                    return Array.isArray(value) ? value[0] : value
                }
                return null
            }, null)

            // Safari ITP Fallback: Read server-set cookies for click IDs
            // These cookies are set by middleware with 90-day expiration,
            // bypassing Safari's 7-day limit on client-side JavaScript cookies.
            const serverCookies = getServerCookieAttribution()

            // Use server cookies as fallback for missing click IDs
            const gclid =
                googleAdsParams.gclid || serverCookies.gclid || undefined
            const fbclid =
                metaPixelTraits.fbclid || serverCookies.fbclid || undefined
            const fbc = metaPixelTraits.fbc || serverCookies.fbc || undefined
            const wbraid =
                googleIosParams.wbraid || serverCookies.wbraid || undefined
            const gbraid =
                googleIosParams.gbraid || serverCookies.gbraid || undefined
            const ttclid =
                tiktokParams.ttclid || serverCookies.ttclid || undefined
            const scCid =
                snapchatParams.ScCid || serverCookies.ScCid || undefined
            const resolvedImpactClickId =
                impactClickId || serverCookies.impactClickId || undefined

            // Extract AppsFlyer params for legacy mapping
            const appsFlyerParams = AppsFlyerLinker.appsFlyerParams(query)

            // Check if we have any attribution data to capture
            const hasAttributionData =
                Object.keys(appsFlyerParams).length > 0 ||
                Object.keys(utmParams).length > 0 ||
                gclid ||
                wbraid ||
                gbraid ||
                fbclid ||
                ttclid ||
                scCid ||
                resolvedImpactClickId

            if (!hasAttributionData) {
                return
            }

            const rawAttributionData = {
                // AppsFlyer params (legacy mapping)
                utm_source: appsFlyerParams.mediaSource,
                utm_campaign: appsFlyerParams.campaign,
                utm_medium: 'website', // Default medium for web traffic
                channel: appsFlyerParams.channel,
                feature: feature ?? appsFlyerParams.feature,
                // All UTM parameters (will override defaults if present in URL)
                ...utmParams,
                // Google Ads parameters (with server cookie fallback)
                gclid,
                wbraid,
                gbraid,
                // Other Google Ads params from URL only
                ...(googleAdsParams.adset && {
                    adset: googleAdsParams.adset,
                }),
                ...(googleAdsParams.network && {
                    network: googleAdsParams.network,
                }),
                ...(googleAdsParams.matchtype && {
                    matchtype: googleAdsParams.matchtype,
                }),
                ...(googleAdsParams.device && {
                    device: googleAdsParams.device,
                }),
                // Meta Pixel traits (with server cookie fallback)
                fbclid,
                fbc,
                fbp: metaPixelTraits.fbp,
                // TikTok (with server cookie fallback)
                ttclid,
                // Snapchat (with server cookie fallback)
                ScCid: scCid,
                // Impact Radius click ID (with server cookie fallback)
                impactClickId: resolvedImpactClickId,
                // AppsFlyer custom data (referral codes, inviter info, etc.)
                ...appsFlyerParams.data,
            }

            // Filter out undefined values to avoid sending noise to analytics
            const attributionData = Object.fromEntries(
                Object.entries(rawAttributionData).filter(
                    ([, value]) => value !== undefined,
                ),
            )

            // Set attribution data in AnalyticsService for Segment/Mixpanel/Braze
            analyticsService.setAttributionData(attributionData)

            // Sync UTM params to GoogleAdsService for GA4 conversions
            // This ensures both services have consistent attribution data
            googleAdsService.setAttributionParams({
                utm_source: attributionData.utm_source as string | undefined,
                utm_medium: attributionData.utm_medium as string | undefined,
                utm_campaign: attributionData.utm_campaign as
                    | string
                    | undefined,
                utm_term: attributionData.utm_term as string | undefined,
                utm_content: attributionData.utm_content as string | undefined,
                adset: attributionData.adset as string | undefined,
                network: attributionData.network as string | undefined,
                matchtype: attributionData.matchtype as string | undefined,
                device: attributionData.device as string | undefined,
            })

            // Pass GCLID to GoogleAdsService for conversion tracking
            // Uses resolved value with server cookie fallback for Safari ITP persistence
            if (gclid) {
                googleAdsService.setGclid(gclid)
            }

            // Pass Impact Radius click ID to AnalyticsService for Segment referrer context
            if (resolvedImpactClickId) {
                analyticsService.setImpactClickId(resolvedImpactClickId)
            }
        }
    }, [feature, query, isReady])

    return null
}

/**
 * Configuration options for the ClickTracker hook
 */
interface ClickTrackerProps {
    /** Override feature name for attribution (e.g., 'homepage', 'referral') */
    feature?: string
    /** Referral information for tracking referral campaigns */
    referralInfo?: ReferralInfo
    /** Additional custom parameters to include in attribution */
    params?: Record<string, unknown>
}
